import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Eye,
  ExternalLink,
  Calendar,
} from "lucide-react";
import StatusBadge from "./StatusBadge";
import PrioritasBadge from "./PrioritasBadge";
import {
  programProgress,
  programRollupStatus,
} from "../../utils/programStorage";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function SubKegiatanRow({ sub, canEdit, onEdit }) {
  const isAudit = sub.specialType === "AUDIT_INVESTIGASI";

  return (
    <div
      data-testid={`sub-row-${sub.id}`}
      className="grid grid-cols-12 items-start gap-3 border-t border-gray-100 px-4 py-3 hover:bg-gray-50/70"
    >
      <div className="col-span-12 md:col-span-5">
        <div className="flex items-start gap-2">
          {sub.kategori ? (
            <span className="mt-0.5 inline-flex h-5 shrink-0 items-center rounded bg-[#e8edf2] px-1.5 text-[10px] font-bold text-[#1e3a5f]">
              {sub.kategori}
            </span>
          ) : null}
          <div className="min-w-0">
            <div className="text-[12.5px] font-medium leading-snug text-slate-900">
              {sub.nama}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
              <span className="font-mono text-[10.5px]">{sub.id}</span>
              {isAudit ? (
                <span className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 border border-amber-200">
                  Audit Investigasi
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-6 md:col-span-3">
        <div className="flex items-center gap-1.5 text-[11.5px] text-slate-600">
          <Calendar className="h-3 w-3 text-slate-400" strokeWidth={2} />
          <span>{formatDate(sub.targetMulai)}</span>
          <span className="text-slate-300">→</span>
          <span>{formatDate(sub.targetSelesai)}</span>
        </div>
        <div className="mt-1">
          <PrioritasBadge prioritas={sub.prioritas} />
        </div>
      </div>

      <div className="col-span-4 md:col-span-2">
        <StatusBadge status={sub.status} />
        <div className="mt-1.5 flex items-center gap-2">
          <div className="h-1.5 w-full max-w-[100px] overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-[#1e3a5f]"
              style={{ width: `${sub.progress || 0}%` }}
            />
          </div>
          <span className="text-[10.5px] font-semibold text-slate-600">
            {sub.progress || 0}%
          </span>
        </div>
      </div>

      <div className="col-span-2 flex justify-end md:col-span-2">
        {isAudit ? (
          <button
            type="button"
            data-testid={`sub-audit-link-${sub.id}`}
            onClick={() => onEdit(sub)}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
          >
            <ExternalLink className="h-3 w-3" strokeWidth={2.4} />
            <span className="hidden sm:inline">Ke Modul Audit</span>
            <span className="sm:hidden">Audit</span>
          </button>
        ) : (
          <button
            type="button"
            data-testid={`sub-edit-${sub.id}`}
            onClick={() => onEdit(sub)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
          >
            {canEdit ? (
              <>
                <Pencil className="h-3 w-3" strokeWidth={2.4} />
                <span>Ubah</span>
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" strokeWidth={2.4} />
                <span>Lihat</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProgramTable({ programs, canEdit, onEditSub }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (programs.length === 0) {
    return (
      <div
        data-testid="program-table-empty"
        className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center"
      >
        <div className="text-[12px] font-semibold text-slate-700">
          Tidak ada program yang cocok dengan filter.
        </div>
        <div className="mt-1 text-[12px] text-slate-500">
          Coba ubah atau reset filter untuk melihat data.
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="program-table"
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      {/* Header */}
      <div className="grid grid-cols-12 gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        <div className="col-span-1">Kode</div>
        <div className="col-span-5">Nama Program</div>
        <div className="col-span-2 text-center">Jml Kegiatan</div>
        <div className="col-span-2">% Selesai</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1 text-right">Aksi</div>
      </div>

      {programs.map((p) => {
        const { percent, done, total } = programProgress(p);
        const rollup = programRollupStatus(p);
        const isOpen = expanded.has(p.id);

        return (
          <div key={p.id} data-testid={`program-row-${p.id}`}>
            <div
              className={`grid grid-cols-12 items-center gap-3 border-b border-gray-100 px-4 py-3 transition-colors hover:bg-gray-50 ${
                isOpen ? "bg-[#1e3a5f]/[0.02]" : ""
              }`}
            >
              <div className="col-span-1">
                <span className="inline-flex items-center rounded bg-[#1e3a5f] px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">
                  {p.kode}
                </span>
              </div>
              <div className="col-span-5 min-w-0">
                <div className="truncate text-[13px] font-semibold text-slate-900" title={p.nama}>
                  {p.nama}
                </div>
              </div>
              <div className="col-span-2 text-center text-[12px] font-semibold text-slate-700">
                {total}
              </div>
              <div className="col-span-2">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full bg-[#1e3a5f]"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-600">
                    {percent}%
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] text-slate-500">
                  {done}/{total} selesai
                </div>
              </div>
              <div className="col-span-1">
                <StatusBadge status={rollup} />
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  data-testid={`program-expand-${p.id}`}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? "Tutup" : "Buka detail"}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-slate-600 transition-colors hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" strokeWidth={2.2} />
                  ) : (
                    <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
                  )}
                </button>
              </div>
            </div>

            {isOpen ? (
              <div
                data-testid={`program-subs-${p.id}`}
                className="animate-fade-in-up border-b border-gray-200 bg-gray-50/50"
              >
                <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Sub-Kegiatan ({p.subKegiatan.length})
                </div>
                {p.subKegiatan.map((s) => (
                  <SubKegiatanRow
                    key={s.id}
                    sub={s}
                    canEdit={canEdit}
                    onEdit={onEditSub}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
